import {
  Component,
  OnInit,
  OnDestroy,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BasicDialogComponent } from '../../shared/components/basic-dialog/basic-dialog.component';
import { SimpleDialog } from '../../shared/models/simple-dialog';
import { CommonModule } from '@angular/common';
import {
  FormsModule,
  ReactiveFormsModule,
  FormGroup,
  FormBuilder,
} from '@angular/forms';
import { ToolbarComponent } from '../../core/layout/components/toolbar/toolbar.component';
import { FooterComponent } from '../../core/layout/components/footer/footer.component';
import { HttpClientModule } from '@angular/common/http';
import { RopaService } from './services/ropa.service';
import { RopaFormBuilderService } from './services/ropa-form-builder.service';
import { RopaUtilsService } from './services/ropa-utils.service';
import { ActivatedRoute, Router } from '@angular/router';
import {
  RopaStep,
  RopaForm,
  FormField,
  FormStep,
  QuestionGroup,
  RopaNotificationEmail,
} from './models/ropa.interface';
import { AuthService } from '../../core/auth/services/auth.service';
import { MatIconModule } from '@angular/material/icon';
import { HtmlTooltipDirective } from '../../shared/components/html-tooltip/html-tooltip.directive';
import { StatusChangeModalComponent, ModalState } from '../../shared/components/az-status-change-modal/status-change-modal.component';

@Component({
  selector: 'app-ropa',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ToolbarComponent,
    FooterComponent,
    HttpClientModule,
    MatIconModule,
    HtmlTooltipDirective,
    StatusChangeModalComponent,
  ],
  templateUrl: './ropa.component.html',
  styleUrls: ['./ropa.component.scss'],
})
export class RopaComponent implements OnInit, OnDestroy {
  currentStep: number = 1;
  isLoading: boolean = false;
  error: string | null = null;
  ropaId: string = '';
  ropaVersion: string = '';
  ropaName: string = '';
  isEditMode: boolean = false;
  isMobileOrTablet: boolean = false;

  // Submission state
  showSubmissionConfirmation: boolean = false;
  showSaveForLaterConfirmation: boolean = false;
  showSaveForLaterResultModal = false;
  saveForLaterResultState: ModalState = 'processing';
  selectedSubmissionType: 'publish' | 'draft' = 'publish';

  steps: RopaStep[] = [
    { number: 1, title: 'General Information', completed: false, active: true },
    { number: 2, title: 'Personal Data', completed: false, active: false },
    { number: 3, title: 'Data Collection', completed: false, active: false },
    { number: 4, title: 'Use & Sharing', completed: false, active: false },
    { number: 5, title: 'Access & Transfers', completed: false, active: false },
    { number: 6, title: 'Security', completed: false, active: false },
  ];

  ropaForm: RopaForm = {} as RopaForm;
  dynamicFormGroup: FormGroup = new FormGroup({});
  prid = '';
  updated_by = '';
  isReadOnly: boolean = false;
  uiMode: 'editing' | 'readonly' | 'new' = 'new';
  private _isSaveForLaterEnabled: boolean = false;
  private initialFormValues: any = null;
  private autosaveInterval: any = null;
  private readonly AUTOSAVE_INTERVAL_MS = 2 * 60 * 1000;

  // Track files that are currently being uploaded
  // Map key is field property name, value is array of uploading file info
  uploadingFiles: Map<string, Array<{ name: string; size: number; uploading: true }>> = new Map();

  // Getter for business process name from FormGroup
  get businessProcessName(): string {
    return this.dynamicFormGroup.get('business_process_name')?.value || '';
  }

  // Getter to check if form has changes from initial values
  get hasFormChanges(): boolean {
    if (!this.initialFormValues || this.uiMode !== 'editing') {
      return false;
    }
    const currentValues = this.dynamicFormGroup.getRawValue();
    const hasChanges =
      JSON.stringify(this.initialFormValues) !== JSON.stringify(currentValues);
    return hasChanges;
  }

  constructor(
    readonly dialog: MatDialog,
    readonly _ropaService: RopaService,
    readonly route: ActivatedRoute,
    readonly router: Router,
    private _authService: AuthService,
    private fb: FormBuilder,
    private ropaFormBuilder: RopaFormBuilderService,
    private ropaUtils: RopaUtilsService,
    private cdr: ChangeDetectorRef
  ) {
    this._authService.amplifyGetUserSession().forEach((session: any) => {
      const { given_name, middle_name } = session.tokens.idToken.payload;
      this.updated_by = `${given_name} ${middle_name}`;
      this.prid = this.ropaUtils.extractPridFromSession(session);
    });
  }

  // Close dropdowns when clicking outside
  @HostListener('document:click', ['$event'])
  onDocumentClick(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.custom-dropdown-container')) {
      this.openDropdowns.clear();
    }
  }

  @HostListener('window:beforeunload', ['$event'])
  saveDraftBeforeUnload($event: any) {
    this.ropaId = this.route.snapshot.params['id'] || '';
    this.ropaVersion = this.route.snapshot.params['version'] || '';

    const isDraft = !!(this.ropaId && !this.ropaVersion);
    this.isEditMode = !!this.ropaId;

    this.detectDevice();
    const editQueryParam = this.route.snapshot.queryParams['edit'];
    if (editQueryParam === 'true') {
      this.uiMode = 'editing';
      this.isReadOnly = false;
    } else if (editQueryParam === 'false') {
      this.uiMode = 'readonly';
      this.isReadOnly = true;
    } else {
      this.uiMode = 'new';
      this.isReadOnly = false;
    }
    if (isDraft && this.uiMode === 'editing') {
      if (this._isSaveForLaterEnabled) {
        this.performAutosave();
      }
    }
  }

  async ngOnInit() {
    // Get the RoPA ID and version from the route (if exists)
    this.ropaId = this.route.snapshot.params['id'] || '';
    this.ropaVersion = this.route.snapshot.params['version'] || '';

    // If we have an ID but no version, it's a draft
    const isDraft = !!(this.ropaId && !this.ropaVersion);
    // isEditMode is true for both projects (with version) and drafts (without version)
    this.isEditMode = !!this.ropaId;

    this.detectDevice();
    // Get the edit query parameter and derive UI mode
    const editQueryParam = this.route.snapshot.queryParams['edit'];
    this.ropaName = this.route.snapshot.queryParams['ropaName'] || '';

    // uiMode controls which set of navigation buttons to show:
    // - 'editing'  => ?edit=true (for both projects and drafts in edit mode)
    // - 'readonly' => ?edit=false (for viewing only)
    // - 'new'      => edit is undefined or not provided (new ROPA)
    if (editQueryParam === 'true') {
      this.uiMode = 'editing';
      this.isReadOnly = false;
    } else if (editQueryParam === 'false') {
      this.uiMode = 'readonly';
      this.isReadOnly = true;
    } else {
      this.uiMode = 'new';
      this.isReadOnly = false;
    }

    await this.loadRopaForm();

    if (isDraft && this.uiMode === 'editing') {
      this.startAutosave();
    }
  }

  ngOnDestroy() {
    this.stopAutosave();
    this.saveDraftBeforeUnload(null);
  }

  private detectDevice() {
    const userAgent = navigator.userAgent;
    const screenWidth = window.innerWidth;

    this.isMobileOrTablet =
      screenWidth <= 1024 ||
      /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
        userAgent
      );
  }

  /**
   * Loads the RoPA form structure from the API
   */
  async loadRopaForm(): Promise<void> {
    this.isLoading = true;
    this.error = null;

    try {
      let form: any;
      this.ropaId = this.route.snapshot.params['id'] || '';

      // If we have an ID but no version, it's a draft
      const isDraft = !!(this.ropaId && !this.ropaVersion);

      if (this.isEditMode || isDraft) {
        // Check if it's a draft (no version) or a project (has version)
        if (this.ropaVersion) {
          form = await this._ropaService.viewRopa(
            this.ropaId,
            this.ropaVersion
          );
          console.log('Loaded draft form:', form);
        } else {
          form = await this._ropaService.viewDraft(this.ropaId, this.prid);
        }
      } else {
        form = await this._ropaService.getRopaForm();
      }

      this.ropaForm = form;
      // TODO: Remove this once backend returns singleSelect in the /forms/structure response
      this.applySingleSelectFields();
      this.applyThirdPartySearchFields();
      this.initializeFormData();
      this.initializeSelectSearchDefaults();
      this.updateStepsFromForm();
      this.isLoading = false;
    } catch (error) {
      console.error('Error loading RoPA form structure:', error);
      this.error = 'Error while loading the form structure. Please try again.';
      this.isLoading = false;
    }
  }

  /**
   * Initializes FormGroup using the form builder service
   */
  private initializeFormData(): void {
    const allQuestionGroups =
      this.ropaForm.steps?.flatMap((step) => step.questionGroups) || [];
    // Pass true if we have an ID (project or draft) to populate form with existing answers
    const hasExistingData = !!this.ropaId;
    this.dynamicFormGroup = this.ropaFormBuilder.createFormGroup(
      allQuestionGroups,
      hasExistingData
    );

    // Transform upload field values from UUIDs to metadata objects
    if (hasExistingData) {
      this.transformUploadFieldValues();
      this.initializeCountryFilterModesFromAnswers();
    }

    this.dynamicFormGroup.valueChanges.subscribe(() => {
      this.updateSaveForLaterState();
    });

    this.updateSaveForLaterState();

    if (this.uiMode === 'editing' && hasExistingData) {
      setTimeout(() => {
        this.initialFormValues = JSON.parse(
          JSON.stringify(this.dynamicFormGroup.getRawValue())
        );
      }, 100);
    }
  }

  private initializeCountryFilterModesFromAnswers(): void {
    const allFields = this.getAllFormFields();

    allFields.forEach(({ field }) => {
      if (!field.otherOptions?.length) return;

      const control = this.getFieldControl(field);
      const values = control?.value;
      if (!Array.isArray(values) || values.length === 0) return;

      const optionValues = new Set((field.options || []).map((option) => option.value));
      const otherOptionValues = new Set(
        (field.otherOptions || []).map((option) => option.value)
      );
      const hasOtherSelections = values.some((value) => otherOptionValues.has(value));
      const hasOptionSelections = values.some(
        (value) => optionValues.has(value) && value !== 'other'
      );

      if (hasOtherSelections) {
        this.countryFilterMode[`field_${field.id}`] = 'other';
        return;
      }

      if (hasOptionSelections) {
        this.countryFilterMode[`field_${field.id}`] = 'legal-entities';
      }
    });
  }

  /**
   * Transforms upload field values from UUID arrays to metadata objects
   * This is needed when loading existing ROPAs where the API returns:
   * - answer: ["uuid1", "uuid2", ...]
   * - options: [{ value: "uuid1", label: { file_name: "...", file_size: ... } }]
   */
  private transformUploadFieldValues(): void {
    const allFields = this.getAllFormFields();
    const uploadFields = allFields.filter(
      (fieldInfo) => fieldInfo.field.type === 'upload'
    );

    uploadFields.forEach((fieldInfo) => {
      const field = fieldInfo.field;
      const control = this.getFieldControl(field);

      if (!control || !field.options) {
        return;
      }

      const currentValue = control.value;

      // Check if the value is an array of UUIDs (strings)
      if (Array.isArray(currentValue) && currentValue.length > 0) {
        const firstItem = currentValue[0];

        // Only transform if values are strings (UUIDs) and not already objects
        if (typeof firstItem === 'string') {
          const transformedValues = currentValue.map((uuid: string) => {
            // Find the corresponding option with this UUID
            const option = field.options?.find((opt) => opt.value === uuid);

            if (option && option.label && typeof option.label === 'object') {
              // Transform to our expected format
              return {
                id: uuid,
                name: (option.label as any).file_name || option.label,
                size: (option.label as any).file_size || 0,
              };
            }

            // Fallback if option not found
            return {
              id: uuid,
              name: 'Unknown file',
              size: 0,
            };
          });

          // Update the control with transformed values
          control.setValue(transformedValues, { emitEvent: false });
        }
      }
    });
  }

  /**
   * Updates steps based on loaded form structure
   */
  private updateStepsFromForm(): void {
    if (this.ropaForm?.steps) {
      this.steps = this.ropaForm.steps.map((step, index) => ({
        number: step.stepNumber,
        title: step.title.replace(
          /^(I{1,3}\.?\s*|V{1,3}\.?\s*|IV\.?\s*|VI\.?\s*)/,
          ''
        ), // Remove Roman numerals
        completed: false,
        active: index === 0,
      }));
    }
  }

  private readonly PIA_URL = 'https://privacy.astrazeneca.net/#/dashboard';
  private readonly TIA_URL = 'https://azcollaboration.sharepoint.com/sites/TSC342/Pages/WelcomeTIA.aspx?CT=1771951351633&OR=OWA-NT-Mail&CID=fe42a828-2458-f1c1-0d8d-1bda9195792c';

  // TODO: Remove once backend returns singleSelect in /forms/structure response
  private readonly SINGLE_SELECT_FIELD_IDS = new Set([67]); // parent_ropa

  private applySingleSelectFields(): void {
    this.ropaForm.steps?.forEach((step) =>
      step.questionGroups.forEach((group) =>
        this.applySingleSelectToGroup(group)
      )
    );

    // Pre-populate selectSearchQueries for single-select fields that already
    // have a saved answer (e.g. loaded from a draft/view).
    const allFields = this.getAllFormFields();
    allFields.forEach(({ field }) => {
      if (!field.singleSelect) return;
      const key = this.getSelectSearchFieldKey(field);
      if (this.selectSearchQueries[key]) return; // already set
      const savedAnswer = (field as any).answer;
      if (typeof savedAnswer === 'string' && savedAnswer.trim() !== '') {
        this.selectSearchQueries[key] = savedAnswer;
        return;
      }

      if (Array.isArray(savedAnswer) && savedAnswer.length > 0) {
        const firstAnswer = savedAnswer[0];
        if (typeof firstAnswer === 'string' && firstAnswer.trim() !== '') {
          this.selectSearchQueries[key] = firstAnswer;
        }
      }
    });
  }

  private applySingleSelectToGroup(group: QuestionGroup): void {
    group.fields?.forEach((field) => {
      if (this.SINGLE_SELECT_FIELD_IDS.has(field.id)) {
        field.singleSelect = true;
      }
    });
    group.subgroups?.forEach((sub) => this.applySingleSelectToGroup(sub));
  }

  // TODO: Remove once backend returns type 'third-party-search' in the form structure
  private applyThirdPartySearchFields(): void {
    this.ropaForm.steps?.forEach((step) =>
      step.questionGroups.forEach((group) =>
        this.applyThirdPartySearchToGroup(group)
      )
    );
  }

  private applyThirdPartySearchToGroup(group: QuestionGroup): void {
    group.fields?.forEach((field) => {
      if (field.modelProperty === 'third_party_data_providers') {
        (field as any).type = 'third-party-search';
      }
    });
    group.subgroups?.forEach((sub) => this.applyThirdPartySearchToGroup(sub));
  }

  private readonly LIA_URL =
    'https://azcollaboration.sharepoint.com/sites/TSM554/Privacy%20Notices%20%20Consents%20Template/Lawful%20Basis%20Guidance/LIA%20Template%20v3.docx?d=w94dfe89ec2dc47ae82568a5c4da4adaa&csf=1&web=1&e=0sHtg1&isSPOFile=1&xsdata=MDV8MDJ8fGNhNjNmN2MwNDU4ODRhMjMyZjllMDhkZTcwOTU2OTM2fGFmOGU4OWEzZDlhYzQyMmZhZDA2Y2M0ZWI0MjE0MzE0fDB8MHw2MzkwNzE5ODQxNTA3ODg5NzJ8VW5rbm93bnxWR1ZoYlhOVFpXTjFjbWwwZVZObGNuWnBZMlY4ZXlKRFFTSTZJbFJsWVcxelgwRlVVRk5sY25acFkyVmZVMUJQVEU5R0lpd2lWaUk2SWpBdU1DNHdNREF3SWl3aVVDSTZJbGRwYmpNeUlpd2lRVTRpT2lKUGRHaGxjaUlzSWxkVUlqb3hNWDA5fDF8TDJOb1lYUnpMekU1T20xbFpYUnBibWRmVGtSU2FVMVVVVE5PUkdkMFQxUmpkMDVwTURCT1YxVXlURlJyZUZwRVRYUlBWMDAwVFRKS2JVNXRWWHBOUjFWNVFIUm9jbVZoWkM1Mk1pOXRaWE56WVdkbGN5OHhOemN4TmpBeE5qRXpPVGszfGU0YWVlM2RjMGRkOTRiMmY5ZTk5MDhkZTcwOTU2OTM1fDgxYzNmN2Y5NTA5MTQ5YjdhOGRlN2JjODljMWY5ODA2&sdata=WGprNmZndE8yUE9QV3ZLcksrN3h3WmR2T0QxSzdEam16VnIzcjI0SUdnUT0%3D&ovuser=af8e89a3-d9ac-422f-ad06-cc4eb4214314%2Ckbdk069%40astrazeneca.net';

  openLegitimateInterestsDialog(): void {
    const data: SimpleDialog = {
      uiTitle: 'Read this before continuing',
      uiMessage: `Please follow this <a href="${this.LIA_URL}" target="_blank" rel="noopener noreferrer">link</a> to perform Legitimate Interest Assessment (LIA): <a href="${this.LIA_URL}" target="_blank" rel="noopener noreferrer">LIA Assessment</a>`,
      showCancel: false,
      showConfirm: true,
      confirmText: 'Continue',
    };
    this.dialog.open(BasicDialogComponent, {
      data,
      width: '640px',
      panelClass: ['custom-dialog-border'],
    });
  }

  openTiaDialog(): void {
    const data: SimpleDialog = {
      uiTitle: 'Read this before continuing',
      uiMessage: `Please follow this <a href="${this.TIA_URL}" target="_blank" rel="noopener noreferrer">link</a> to perform Transfer Impact Assessment (TIA): <a href="${this.TIA_URL}" target="_blank" rel="noopener noreferrer">Transfer Impact Assessment (TIA)</a>`,
      showCancel: false,
      showConfirm: true,
      confirmText: 'Continue',
    };
    this.dialog.open(BasicDialogComponent, {
      data,
      width: '640px',
      panelClass: ['custom-dialog-border'],
    });
  }

  openPiaDialog(): void {
    const data: SimpleDialog = {
      uiTitle: 'Read this before continuing',
      uiMessage: `Please follow this <a href="${this.PIA_URL}" target="_blank" rel="noopener noreferrer">link</a> to perform Privacy Impact Assessment (PIA): <a href="${this.PIA_URL}" target="_blank" rel="noopener noreferrer">Assessment (PIA)</a>`,
      showCancel: false,
      showConfirm: true,
      confirmText: 'Continue',
    };
    this.dialog.open(BasicDialogComponent, {
      data,
      width: '640px',
      panelClass: ['custom-dialog-border'],
    });
  }

  openCancelDialog(): void {
    const data: SimpleDialog = {
      uiTitle: 'Are you sure you want to close the process?',
      uiMessage: '',
      redirect: '/dashboard',
      showCancel: true,
      showConfirm: true,
      cancelText: 'Cancel',
      confirmText: 'Confirm',
    };
    const dialogRef = this.dialog.open(BasicDialogComponent, {
      data,
      width: '640px',
      panelClass: ['cancel-confirm-dialog', 'custom-dialog-border'],
    });
    dialogRef.afterClosed().subscribe(() => {});
  }

  // Resolve the property name to store/retrieve values. Uses modelProperty since it's required now.
  private resolveProp(field: FormField): string {
    return field.modelProperty;
  }

  // Check if description contains HTML
  public isHtmlDescription(description: string | undefined): boolean {
    return this.ropaUtils.isHtmlDescription(description);
  }

  getCurrentStepData(): FormStep | undefined {
    return this.ropaForm.steps?.find(
      (step) => step.stepNumber === this.currentStep
    );
  }

  onFieldChange(field: FormField, value: any): void {
    const prop = this.resolveProp(field);
    if (!prop) return;

    const control = this.dynamicFormGroup.get(prop);
    if (!control) return;

    if (field.type === 'checkbox') {
      const currentValues = (control.value as string[]) || [];
      const index = currentValues.indexOf(value);
      if (index > -1) {
        currentValues.splice(index, 1);
      } else {
        currentValues.push(value);
        if (value === 'legitimate_interests' && field.modelProperty === 'lawful_basis_personal_data') {
          this.openLegitimateInterestsDialog();
        }
        const piaSubjects = ['employees_candidates', 'patients_research_subjects', 'vulnerable_individuals'];
        if (field.modelProperty === 'personal_data_subjects' && piaSubjects.includes(value)) {
          this.openPiaDialog();
        }
      }
      control.setValue([...currentValues]);
    } else if (field.type === 'dropdown') {
      // Handle dropdown select event
      if (value.target && value.target.selectedOptions) {
        const selectedValues = Array.from(value.target.selectedOptions).map(
          (option: any) => option.value
        );
        control.setValue(selectedValues);
      } else {
        // Fallback for single value
        const currentValues = (control.value as string[]) || [];
        const index = currentValues.indexOf(value);
        if (index > -1) {
          currentValues.splice(index, 1);
        } else {
          currentValues.push(value);
        }
        control.setValue([...currentValues]);
      }
    } else {
      control.setValue(value);
      const piaRadioFields = ['high_volume_processing', 'high_risk_processing', 'individuals_rights_exercise'];
      if (piaRadioFields.includes(field.modelProperty) && value === 'yes') {
        this.openPiaDialog();
      }
      if (field.modelProperty === 'international_data_sharing' && value === 'yes') {
        this.openTiaDialog();
      }
      if (field.modelProperty === 'lawful_basis_sensitive_data' && value !== 'not_applicable' && value !== '') {
        this.openPiaDialog();
      }
    }
    this.updateSaveForLaterState();
  }

  getFieldValue(field: FormField): any {
    const prop = this.resolveProp(field);
    if (!prop) return undefined;

    const control = this.dynamicFormGroup.get(prop);
    const value = control?.value;

    return value;
  }

  isCheckboxChecked(field: FormField, optionValue: string): boolean {
    if (field.type !== 'checkbox' && field.type !== 'dropdown') return false;
    const prop = this.resolveProp(field);
    if (!prop) return false;
    const control = this.dynamicFormGroup.get(prop);
    const values = (control?.value as string[]) || [];
    return values.includes(optionValue);
  }

  isDropdownSelected(field: FormField, optionValue: string): boolean {
    if (field.type !== 'dropdown') return false;
    const prop = this.resolveProp(field);
    if (!prop) return false;
    const control = this.dynamicFormGroup.get(prop);
    const values = (control?.value as string[]) || [];
    return values.includes(optionValue);
  }

  getDropdownDisplayText(field: FormField): string {
    const prop = this.resolveProp(field);
    if (!prop) return field.placeholder || 'Select options';
    const control = this.dynamicFormGroup.get(prop);
    const selectedValues = (control?.value as string[]) || [];

    if (selectedValues.length === 0) {
      return field.placeholder || 'Select options';
    }

    if (selectedValues.length === 1) {
      const allOptions = [
        ...(field.options || []),
        ...(field.otherOptions || []),
      ];
      const option = allOptions.find((opt) => opt.value === selectedValues[0]);
      return option?.label || selectedValues[0];
    }

    return `${selectedValues.length} options selected`;
  }

  goToStep(stepNumber: number): void {
    if (stepNumber >= 1 && stepNumber <= this.ropaForm.steps.length) {
      this.currentStep = stepNumber;
      this.updateStepStatus();
      // Scroll to top of page when navigating to specific step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  nextStep(): void {
    if (this.currentStep < this.ropaForm.steps.length) {
      this.steps[this.currentStep - 1].completed = true;
      this.currentStep++;
      this.updateStepStatus();
      // Scroll to top of page when navigating to next step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  previousStep(): void {
    if (this.currentStep > 1) {
      this.currentStep--;
      this.updateStepStatus();
      // Scroll to top of page when navigating to previous step
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
  }

  public updateStepStatus(): void {
    this.steps.forEach((step, index) => {
      step.active = step.number === this.currentStep;
      if (step.number < this.currentStep) {
        step.completed = true;
      }
    });
  }

  onCancel(): void {
    this.openCancelDialog();
  }

  onNext(): void {
    if (this.currentStep === this.ropaForm.steps.length) {
      // If we're on the last step, show confirmation page instead of moving to next step
      this.showSubmissionConfirmation = true;
      // Scroll to top of page when showing confirmation
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } else {
      // Otherwise, proceed to next step normally
      this.nextStep();
    }
  }

  // Submission confirmation page methods
  onSubmissionCancel(): void {
    // Navigate to dashboard
    this.router.navigate(['/dashboard']);
  }

  onSubmissionBack(): void {
    // Go back to the last step of the form
    this.showSubmissionConfirmation = false;
    this.currentStep = this.ropaForm.steps.length;
    this.updateStepStatus();
  }

  onSubmissionContinue(): void {
    this.openSubmissionConfirmationDialog();
  }

  onDraftSubmission(): void {
    this.openSaveForLaterConfirmationDialog();
  }

  openSaveForLaterMandatoryFieldsDialog(): void {
    const data: SimpleDialog = {
      uiTitle: 'Incomplete Required Fields',
      uiMessage: `Please enter the <strong>Name of the business process</strong> the <strong>SET area</strong> and <strong>Name of Business Process Owner</strong> to save your draft. These fields are required for draft saving. The fields are located in <strong>Section 1 - General Information</strong>.`,
      showConfirm: true,
      showCancel: false,
      confirmText: 'Close',
    };

    this.dialog.open(BasicDialogComponent, {
      data,
      width: '640px',
      panelClass: ['mandatory-fields-dialog', 'custom-dialog-border'],
    });
  }

  openSubmissionConfirmationDialog(): void {
    const data: SimpleDialog = {
      uiTitle: 'Confirm Submission',
      uiMessage: `Are you sure you want to ${
        this.selectedSubmissionType === 'publish' ? 'publish' : 'save as draft'
      } this ${this.isEditMode ? 'updated' : 'new'} ROPA form?`,
      showCancel: true,
      showConfirm: true,
      cancelText: 'Cancel',
      confirmText: 'Confirm',
    };

    const dialogRef = this.dialog.open(BasicDialogComponent, {
      data,
      width: '640px',
      panelClass: ['submission-confirm-dialog', 'custom-dialog-border'],
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        // User confirmed, proceed with submission
        this.proceedWithSubmission();
      }
      // If result is falsy (cancelled), do nothing - dialog just closes
    });
  }

  async proceedWithSubmission(): Promise<void> {
    try {
      const submissionData = this.buildSubmissionData();

      // Add submission type to the data
      submissionData.submissionType = this.selectedSubmissionType;

      let response;
      if (this.isEditMode) {
        // Update existing form
        submissionData.version = this.ropaVersion;
        submissionData.project_id = this.ropaId;
        response = await this._ropaService.createNewVersionForm(submissionData);
        console.log('Form updated successfully');
      } else {
        // Create new form
        response = await this._ropaService.submitRopaForm(submissionData);
        console.log('Form submitted successfully');
      }

      try {
        const emailPayload: RopaNotificationEmail = {
          project_id: response.project_id,
          title: submissionData.title,
          created_by: this.updated_by,
          created_at: response.created_at,
          version: response.version_number,
          email_type: this.isEditMode ? 'update' : 'create',
        };
        // Fire and await so failures can be logged but not interrupt UX
        await this._ropaService.sendRopaNotificationEmail(emailPayload);
      } catch (notifyErr) {
        console.warn('Failed to send notification email:', notifyErr);
      }

      // Handle successful submission (redirect, show success message, etc.)
      // Navigate to dashboard after successful submission
      this.router.navigate(['/dashboard']);
    } catch (error) {
      console.error('Error submitting form:', error);
      this.error = 'Error while sending the form. Please try again.';
      this.showSubmissionConfirmation = false;
    }
  }

  async saveDraft(): Promise<void> {
    this.saveForLaterResultState = 'processing';
    this.showSaveForLaterResultModal = true;
    try {
      const draftData = this.buildSubmissionData(true);
      await this._ropaService.submitDraftRopaForm(draftData);
      this.saveForLaterResultState = 'success-with-message';
    } catch (error) {
      console.error('Error submitting form:', error);
      this.saveForLaterResultState = 'error';
    }
  }

  onSubmissionTypeChange(type: 'publish' | 'draft'): void {
    this.selectedSubmissionType = type;
  }

  /**
   * Builds the required data structure for form submission
   */
  private buildSubmissionData(isDraft: boolean = false): any {
    const responses: any[] = [];

    // Iterate through all steps to build responses
    this.ropaForm.steps.forEach((step) => {
      step.questionGroups.forEach((group) => {
        this.processGroupFields(group, step.stepNumber, responses);
      });
    });

    const formValues = this.dynamicFormGroup.value;

    // Determine the ropa field value for draft submissions:
    // - If creating a new ROPA (no ropaId), set ropa to null
    // - If creating a new version from existing ROPA, use the loaded form's ropa name

    if (isDraft) {
      let titleValue = formValues['business_process_name'];
      let ropaValue = null;
      if (this.ropaId) {
        ropaValue = `ROPA-${this.ropaId}`;
      } else {
        ropaValue = `ROPA-${titleValue}`;
      }

      const parentRopaId: number | undefined =
        this.selectSearchSelectedValues['field_67']?.[0]?.id;

      return {
        title: titleValue || `ROPA-${new Date().toLocaleDateString()}`,
        form_id: this.ropaForm.form_id,
        prid: this.prid, // Here you should get the current user ID
        ...(parentRopaId !== undefined ? { parent_ropa_id: parentRopaId } : {}),
        responses: responses,
        ropa: ropaValue, // null for new drafts, original ropa name for versions
      };
    }

    const parentRopaId: number | undefined =
      this.selectSearchSelectedValues['field_67']?.[0]?.id;

    return {
      title:
        formValues['business_process_name'] ||
        `ROPA-${new Date().toLocaleDateString()}`,
      form_id: this.ropaForm.form_id,
      prid: this.prid, // Here you should get the current user ID
      ...(parentRopaId !== undefined ? { parent_ropa_id: parentRopaId } : {}),
      responses: responses,
    };
  }

  /**
   * Processes group fields (and subgroups) to build responses
   */
  private processGroupFields(
    group: QuestionGroup,
    stepNumber: number,
    responses: any[]
  ): void {
    // Process current group fields
    group.fields.forEach((field) => {
      const fieldValue = this.getFieldValue(field);

      // Only add fields that have a value
      if (this.hasValue(fieldValue, field.type)) {
        const response = {
          question_id: field.id,
          answer_type: field.type,
          answer_value: this.formatAnswerValue(fieldValue, field.type),
        };
        responses.push(response);
      }
    });

    // Process subgroups recursively
    group.subgroups.forEach((subgroup) => {
      this.processGroupFields(subgroup, stepNumber, responses);
    });
  }

  /**
   * Checks if a field has a value
   */
  private hasValue(value: any, fieldType: string): boolean {
    if (value === null || value === undefined) return false;

    if (fieldType === 'checkbox' || fieldType === 'upload') {
      return Array.isArray(value) && value.length > 0;
    }

    if (typeof value === 'string') {
      return value.trim().length > 0;
    }

    // For arrays in general, check if they have content
    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  }

  /**
   * Formats the response value according to field type
   */
  private formatAnswerValue(value: any, fieldType: string): any {
    switch (fieldType) {
      case 'checkbox':
        // For checkboxes, return array of selected values only if it has content
        if (Array.isArray(value) && value.length > 0) {
          return value;
        }
        return Array.isArray(value) ? value : [];
      case 'upload':
        if (Array.isArray(value) && value.length > 0) {
          return value.map((item: any) => {
            if (item && typeof item === 'object' && 'id' in item) {
              return item.id;
            }
            return item;
          });
        }
        return Array.isArray(value) ? value : [];
      case 'text':
      case 'email':
      case 'textarea':
      case 'radio':
      case 'select':
      default:
        // For other types, return value as string
        return value;
    }
  }

  // Dropdown enhancement properties and methods
  openDropdowns: Set<string> = new Set();
  searchTerms: { [key: string]: string } = {};

  // Country filter mode for fields with otherOptions (e.g. regions_activity)
  countryFilterMode: { [fieldKey: string]: 'legal-entities' | 'other' | null } = {};

  getCountryFilterMode(field: FormField): 'legal-entities' | 'other' | null {
    const key = `field_${field.id}`;
    return this.countryFilterMode[key] ?? null;
  }

  onCountryFilterModeChange(
    field: FormField,
    mode: 'legal-entities' | 'other'
  ): void {
    const key = `field_${field.id}`;
    if (this.countryFilterMode[key] === mode) return;
    this.countryFilterMode[key] = mode;
    // Keep current selection when switching modes so both lists can be combined
    this.openDropdowns.delete(`field_${field.id}`);
    this.searchTerms[`field_${field.id}`] = '';
  }

  // Select-search properties and methods
  selectSearchQueries: { [key: string]: string } = {};
  selectSearchOptions: { [key: string]: any[] } = {};
  selectSearchLoading: { [key: string]: boolean } = {};
  selectSearchDropdownOpen: { [key: string]: boolean } = {};
  selectSearchSelectedValues: { [key: string]: any[] } = {}; // Changed to array
  selectSearchTimeouts: { [key: string]: any } = {};
  selectSearchChildQuestionIds: { [key: string]: number } = {};
  selectSearchValueMappings: {
    [key: string]: { [optionValue: string]: string };
  } = {}; // Track mappings

  isDropdownOpen(fieldId: string): boolean {
    return this.openDropdowns.has(fieldId);
  }

  toggleDropdown(fieldId: string): void {
    if (this.openDropdowns.has(fieldId)) {
      this.openDropdowns.delete(fieldId);
    } else {
      // Close all other dropdowns first
      this.openDropdowns.clear();
      this.openDropdowns.add(fieldId);
    }
  }

  getSearchTerm(fieldId: string): string {
    return this.searchTerms[fieldId] || '';
  }

  onSearchTermChange(fieldId: string, term: string): void {
    this.searchTerms[fieldId] = term;
  }

  getFilteredOptions(field: FormField, fieldId: string): any[] {
    // For fields that have a country filter mode, swap the source list
    const fieldKey = `field_${field.id}`;
    const mode = this.countryFilterMode[fieldKey] ?? null;
    const sourceOptions =
      mode === 'other' && field.otherOptions?.length
        ? field.otherOptions
        : field.options;

    if (!sourceOptions) return [];

    // When in legal-entities mode (or no mode yet), exclude the sentinel "Other" option
    // so it doesn't appear as a selectable item now that radio buttons handle that switch
    const excludeOther = field.otherOptions?.length && mode !== 'other';
    let filtered = excludeOther
      ? sourceOptions.filter((option) => option.label?.toLowerCase() !== 'other')
      : sourceOptions;

    // When showing "Other" countries, exclude any already present in Operating Countries
    // (field.options), comparing by label since both lists use different value formats.
    if (mode === 'other' && field.options?.length) {
      const operatingLabels = new Set(
        field.options
          .filter((o) => o.label?.toLowerCase() !== 'other')
          .map((o) => (o.label ?? '').toLowerCase())
      );
      filtered = filtered.filter((o) => !operatingLabels.has((o.label ?? '').toLowerCase()));
    }

    const searchTerm = this.getSearchTerm(fieldId).toLowerCase();
    if (!searchTerm) return filtered;

    return filtered.filter((option) =>
      (option.label ?? '').toLowerCase().includes(searchTerm)
    );
  }

  /** Returns true when the 'other' sentinel value is currently selected for a field with otherOptions. */
  isOtherOptionSelected(field: FormField): boolean {
    if (!field.otherOptions?.length) return false;
    const prop = this.resolveProp(field);
    const control = this.dynamicFormGroup.get(prop);
    const values = (control?.value as string[]) || [];
    return values.includes('other');
  }

  onDropdownOptionChange(field: FormField, optionValue: string): void {
    const control = this.getFieldControl(field);
    if (!control) return;

    const currentValues = (control.value as string[]) || [];
    const index = currentValues.indexOf(optionValue);

    if (index > -1) {
      // Remove the option if it's already selected
      currentValues.splice(index, 1);
      // When unchecking 'other', also clear any selected otherOptions values
      if (optionValue === 'other' && field.otherOptions?.length) {
        const otherOptionValues = new Set(field.otherOptions.map((o) => o.value));
        control.setValue(currentValues.filter((v) => !otherOptionValues.has(v)));
        return;
      }
    } else {
      // Add the option
      currentValues.push(optionValue);
    }

    control.setValue([...currentValues]);
  }

  /**
   * Helper method to get FormControl for a field
   */
  private getFieldControl(field: FormField) {
    const prop = this.resolveProp(field);
    return prop ? this.dynamicFormGroup.get(prop) : null;
  }

  onDropdownCheckboxClick(
    event: Event,
    field: FormField,
    optionValue: string
  ): void {
    const checkbox = event.target as HTMLInputElement;
    const prop = this.resolveProp(field);
    if (!prop) {
      event.preventDefault();
      return;
    }

    const control = this.dynamicFormGroup.get(prop);
    if (!control) {
      event.preventDefault();
      return;
    }

    const currentValues = (control.value as string[]) || [];
    const isCurrentlySelected = currentValues.includes(optionValue);

    // Call the original method to handle the selection
    this.onDropdownOptionChange(field, optionValue);
  }

  /** Returns the active option list for a field, respecting the current country filter mode. */
  private getActiveOptions(field: FormField): any[] {
    const key = `field_${field.id}`;
    const mode = this.countryFilterMode[key] ?? null;
    return mode === 'other' && field.otherOptions?.length
      ? field.otherOptions
      : (field.options ?? []);
  }

  areAllOptionsSelected(field: FormField): boolean {
    const activeOptions = this.getActiveOptions(field);
    if (activeOptions.length === 0) return false;

    const prop = this.resolveProp(field);
    if (!prop) return false;

    const control = this.dynamicFormGroup.get(prop);
    const selectedValues = (control?.value as string[]) || [];
    return activeOptions.every((o) => selectedValues.includes(o.value));
  }

  toggleAllOptions(field: FormField): void {
    const control = this.getFieldControl(field);
    if (!control) return;

    if (this.areAllOptionsSelected(field)) {
      // Deselect all options from the active list
      const activeValues = this.getActiveOptions(field).map((o) => o.value);
      const currentValues = (control.value as string[]) || [];
      control.setValue(currentValues.filter((v) => !activeValues.includes(v)));
    } else {
      this.selectAllOptions(field, control);
    }
  }

  /**
   * Selects all options for a field, respecting the current country filter mode.
   */
  private selectAllOptions(field: FormField, control: any): void {
    const activeValues = this.getActiveOptions(field).map((o) => o.value);
    const currentValues = (control.value as string[]) || [];
    // Merge: keep already-selected values from the other list, add all from active list
    const merged = Array.from(new Set([...currentValues, ...activeValues]));
    control.setValue(merged);
  }

  resetDropdownFilter(field: FormField): void {
    const prop = this.resolveProp(field);
    if (!prop) return;

    const control = this.dynamicFormGroup.get(prop);
    if (!control) return;

    control.setValue([]);
  }

  getSelectedOptions(field: FormField): any[] {
    const prop = this.resolveProp(field);
    if (!prop) return [];

    const control = this.dynamicFormGroup.get(prop);
    const selectedValues = (control?.value as string[]) || [];
    const allOptions = [
      ...(field.options || []),
      ...(field.otherOptions || []),
    ];
    return allOptions.filter((option) => selectedValues.includes(option.value));
  }

  removeSelectedOption(field: FormField, optionValue: string): void {
    const prop = this.resolveProp(field);
    if (!prop) return;

    const control = this.dynamicFormGroup.get(prop);
    if (!control) return;

    const currentValues = (control.value as string[]) || [];
    const index = currentValues.indexOf(optionValue);

    if (index > -1) {
      currentValues.splice(index, 1);
      control.setValue([...currentValues]);
      control.markAsDirty();
      control.markAsTouched();
      this.dynamicFormGroup.updateValueAndValidity();
      this.cdr.detectChanges();
    }
  }

  // Upload functionality
  dragOverFields: Set<string> = new Set();

  isDragOver(fieldId: string): boolean {
    return this.dragOverFields.has(fieldId);
  }

  onDragOver(event: DragEvent, fieldId: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOverFields.add(fieldId);
  }

  onDragLeave(event: DragEvent, fieldId: string): void {
    event.preventDefault();
    event.stopPropagation();
    this.dragOverFields.delete(fieldId);
  }

  async onDrop(event: DragEvent, field: FormField): Promise<void> {
    event.preventDefault();
    event.stopPropagation();

    const fieldId = 'field_' + field.id;
    this.dragOverFields.delete(fieldId);

    const files = Array.from(event.dataTransfer?.files || []);
    try {
      await this.handleFileUpload(field, files);
    } catch (error) {
      console.error('Error handling file drop:', error);
    }
  }

  async onFileSelected(event: Event, field: FormField): Promise<void> {
    const input = event.target as HTMLInputElement;
    const files = Array.from(input.files || []);
    try {
      await this.handleFileUpload(field, files);
    } catch (error) {
      console.error('Error handling file selection:', error);
    }
  }

  async handleFileUpload(field: FormField, files: File[]): Promise<void> {
    const prop = this.resolveProp(field);
    if (!prop) return;

    const control = this.dynamicFormGroup.get(prop);
    if (!control) return;

    // Validate files
    const validFiles = files.filter((file) =>
      this.ropaUtils.validateFile(file)
    );

    if (validFiles.length === 0) return;

    // Add all files to uploading state immediately
    const uploadingFilesForField = validFiles.map(file => ({
      name: file.name,
      size: file.size,
      uploading: true as const
    }));

    this.uploadingFiles.set(prop, [
      ...(this.uploadingFiles.get(prop) || []),
      ...uploadingFilesForField
    ]);
    this.cdr.detectChanges();

    // Upload each file individually and add to control immediately after upload
    // This prevents race conditions when multiple files are selected quickly
    for (const file of validFiles) {
      try {
        const uploadRequest = {
          file_name: file.name,
          file_size: file.size,
        };

        const uploadResponse = await this._ropaService.generateUploadUrl(
          uploadRequest
        );

        if (!uploadResponse) {
          throw new Error('Failed to generate upload URL');
        }

        const document_id = uploadResponse.document_id;

        await this._ropaService.uploadToS3(file, uploadResponse);
        await this._ropaService.confirmUpload(document_id);

        // Remove from uploading state
        this.removeFromUploadingState(prop, file.name);

        // Add the uploaded file metadata to the control
        const currentValue = control.value || [];
        const existingUploads = Array.isArray(currentValue)
          ? currentValue.filter(
              (item: any) =>
                item &&
                typeof item === 'object' &&
                'id' in item &&
                !(item instanceof File)
            )
          : [];

        const newUpload = {
          id: document_id,
          name: file.name,
          size: file.size,
        };

        control.setValue([...existingUploads, newUpload]);

        // Trigger change detection to update the UI immediately
        this.cdr.detectChanges();
      } catch (error) {
        console.error(`Error uploading file ${file.name}:`, error);
        // Remove from uploading state even on error
        this.removeFromUploadingState(prop, file.name);
        this.cdr.detectChanges();
        // Continue with other files even if one fails
      }
    }
  }

  /**
   * Remove a file from the uploading state
   */
  private removeFromUploadingState(fieldProp: string, fileName: string): void {
    const current = this.uploadingFiles.get(fieldProp) || [];
    const updated = current.filter(f => f.name !== fileName);
    if (updated.length === 0) {
      this.uploadingFiles.delete(fieldProp);
    } else {
      this.uploadingFiles.set(fieldProp, updated);
    }
  }

  /**
   * Get all files for a field including both uploaded and currently uploading
   */
  getAllFilesForField(
    field: FormField
  ): Array<{ id?: string | number; name: string; size: number; uploading?: boolean }> {
    const prop = this.resolveProp(field);
    if (!prop) return [];

    const uploadedFiles = this.getUploadedFiles(field);
    const uploadingFilesForField = this.uploadingFiles.get(prop) || [];

    return [...uploadedFiles, ...uploadingFilesForField];
  }

  /**
   * Check if a file is currently uploading
   */
  isFileUploading(file: any): boolean {
    return file.uploading === true;
  }

  getUploadedFiles(
    field: FormField
  ): Array<File | { id: string | number; name: string; size: number }> {
    const prop = this.resolveProp(field);
    if (!prop) return [];

    const control = this.dynamicFormGroup.get(prop);
    return (
      (control?.value as Array<
        File | { id: string | number; name: string; size: number }
      >) || []
    );
  }

  removeUploadedFile(field: FormField, index: number): void {
    const prop = this.resolveProp(field);
    if (!prop) return;

    const control = this.dynamicFormGroup.get(prop);
    if (!control) return;

    // Get all files to determine which one to remove
    const allFiles = this.getAllFilesForField(field);
    const fileToRemove = allFiles[index];

    // Can't remove files that are currently uploading
    if (fileToRemove && this.isFileUploading(fileToRemove)) {
      return;
    }

    // Calculate the correct index in the uploaded files array
    const uploadedFiles = this.getUploadedFiles(field);
    const uploadedIndex = uploadedFiles.findIndex(
      f => !this.isFileUploading(f) &&
           f.name === fileToRemove.name &&
           f.size === fileToRemove.size
    );

    if (uploadedIndex !== -1 && Array.isArray(uploadedFiles)) {
      const updatedFiles = [...uploadedFiles];
      updatedFiles.splice(uploadedIndex, 1);
      control.setValue(updatedFiles);
    }
  }

  async downloadUploadedFile(fileId: any): Promise<void> {
    try {
      const response = await this._ropaService.generateDownloadUrl(fileId.id);

      if (response && response.download_url && response.file_name) {
        let fileName = response.file_name;

        const link = document.createElement('a');
        link.href = response.download_url;
        link.download = fileName;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';

        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        console.error(
          'Invalid response from download URL generation:',
          response
        );
      }
    } catch (error) {
      console.error('Error downloading file:', error);
    }
  }

  // Format file size (delegates to utils service)
  formatFileSize(bytes: number): string {
    return this.ropaUtils.formatFileSize(bytes);
  }

  /**
   * Uploads all files from upload fields to the backend
   * Uses presigned URL flow: generate URL -> upload to S3 -> confirm upload
   * Replaces File objects with S3 URLs/file IDs in form controls
   */
  async uploadAllFiles(): Promise<void> {
    const allFields = this.getAllFormFields();
    const uploadFields = allFields.filter(
      (fieldInfo) => fieldInfo.field.type === 'upload'
    );

    for (const fieldInfo of uploadFields) {
      const files = this.getUploadedFiles(fieldInfo.field);

      // Check if there are any File objects in the array
      const hasFileObjects = files.some((f) => f instanceof File);

      if (files.length > 0 && hasFileObjects) {
        try {
          const filesToUpload = files.filter(
            (f): f is File => f instanceof File
          );

          const uploadedFilesData: Array<{
            id: number;
            name: string;
            size: number;
          }> = [];

          for (const file of filesToUpload) {
            const uploadRequest = {
              file_name: file.name,
              file_size: file.size,
            };

            const uploadResponse = await this._ropaService.generateUploadUrl(
              uploadRequest
            );

            if (!uploadResponse) {
              throw new Error('Failed to generate upload URL');
            }

            const document_id = uploadResponse.document_id;

            await this._ropaService.uploadToS3(file, uploadResponse);
            await this._ropaService.confirmUpload(document_id);

            uploadedFilesData.push({
              id: document_id,
              name: file.name,
              size: file.size,
            });
          }

          const control = this.getFieldControl(fieldInfo.field);
          if (control && uploadedFilesData.length > 0) {
            const currentValue = control.value || [];
            const existingUploads = Array.isArray(currentValue)
              ? currentValue.filter(
                  (item: any) =>
                    item &&
                    typeof item === 'object' &&
                    'id' in item &&
                    !(item instanceof File)
                )
              : [];

            const allUploads = [...existingUploads, ...uploadedFilesData];
            control.setValue(allUploads);
          }
        } catch (error) {
          console.error(
            `Error uploading files for field ${fieldInfo.field.id}:`,
            error
          );
          throw new Error(
            `Failed to upload files for ${fieldInfo.field.label}. Please try again.`
          );
        }
      }
    }
  }

  /**
   * Checks if all required fields in the current step are completed
   */
  isCurrentStepValid(): boolean {
    const currentFormStep = this.getCurrentStepData();
    if (!currentFormStep) return false;

    // Get all field properties for current step
    const fieldProperties = this.getFieldPropertiesFromGroups(
      currentFormStep.questionGroups
    );

    // Check if all required fields are valid
    return fieldProperties.every((prop) => {
      const control = this.dynamicFormGroup.get(prop);
      return control ? control.valid : true;
    });
  }

  /**
   * Gets all field properties from question groups recursively
   */
  private getFieldPropertiesFromGroups(groups: QuestionGroup[]): string[] {
    const properties: string[] = [];

    groups.forEach((group) => {
      // Add field properties from current group
      group.fields?.forEach((field) => {
        if (field.modelProperty && field.required) {
          properties.push(field.modelProperty);
        }
      });

      // Add properties from subgroups
      if (group.subgroups?.length) {
        properties.push(...this.getFieldPropertiesFromGroups(group.subgroups));
      }
    });

    return properties;
  }

  // Select-search methods
  getSelectSearchFieldKey(field: FormField): string {
    return `field_${field.id}`;
  }

  getSelectSearchQuery(field: FormField): string {
    const key = this.getSelectSearchFieldKey(field);
    return this.selectSearchQueries[key] || '';
  }

  getSelectSearchOptions(field: FormField): any[] {
    const key = this.getSelectSearchFieldKey(field);
    return this.selectSearchOptions[key] || [];
  }

  isSelectSearchLoading(field: FormField): boolean {
    const key = this.getSelectSearchFieldKey(field);
    return this.selectSearchLoading[key] || false;
  }

  isSelectSearchDropdownOpen(field: FormField): boolean {
    const key = this.getSelectSearchFieldKey(field);
    return this.selectSearchDropdownOpen[key] || false;
  }

  isSelectSearchSingleMode(field: FormField): boolean {
    return !!field.singleSelect;
  }

  clearSelectSearchSingleValue(field: FormField): void {
    const key = this.getSelectSearchFieldKey(field);
    this.selectSearchSelectedValues[key] = [];
    this.selectSearchQueries[key] = '';
    const control = this.getFieldControl(field);
    if (control) control.setValue('');
  }

  getSelectSearchDisplayValue(field: FormField): string {
    const key = this.getSelectSearchFieldKey(field);
    const selectedValues = this.selectSearchSelectedValues[key] || [];

    if (selectedValues.length > 0) {
      // Show placeholder text when items are selected (they'll be shown below)
      return `${selectedValues.length} item(s) selected`;
    }
    // If there's a typed query already, show it
    if (this.selectSearchQueries[key]) {
      return this.selectSearchQueries[key];
    }
    // Fallback: use existing form control value (e.g., when loading edit/view mode)
    const control = this.getFieldControl(field);
    const controlValue = control?.value;
    if (
      controlValue &&
      Array.isArray(controlValue) &&
      controlValue.length > 0
    ) {
      // Try to map to existing loaded option labels if present
      const options = this.selectSearchOptions[key] || field.options || [];
      const matches = controlValue.map((value) => {
        const match = options.find((opt: any) => opt.value === value);
        return match || { value, label: value };
      });
      // seed cached selection so later logic works consistently
      this.selectSearchSelectedValues[key] = matches;
      return `${matches.length} item(s) selected`;
    }

    return '';
  }

  onSelectSearchInput(field: FormField, value: string): void {
    const key = this.getSelectSearchFieldKey(field);
    this.selectSearchQueries[key] = value;

    // Don't clear selected values when typing (for multi-select)
    // User can remove individual selections if needed

    // Clear existing timeout
    if (this.selectSearchTimeouts[key]) {
      clearTimeout(this.selectSearchTimeouts[key]);
    }

    // Only search if query has at least 2 characters
    const minLength = 2;
    if (value.length >= minLength) {
      this.selectSearchDropdownOpen[key] = true;
      // Set timeout to debounce the search
      this.selectSearchTimeouts[key] = setTimeout(() => {
        if (field.type === 'third-party-search') {
          this.performThirdPartySearch(field, value);
        } else {
          this.performSelectSearch(field, value);
        }
      }, 300);
    } else {
      this.selectSearchDropdownOpen[key] = false;
      this.selectSearchOptions[key] = [];
    }
  }

  /**
   * Seeds select-search display values for existing form controls (edit/view modes)
   */
  private initializeSelectSearchDefaults(): void {
    if (!this.ropaForm?.steps) return;
    // Iterate all fields to find select-search types
    this.ropaForm.steps.forEach((step) => {
      step.questionGroups.forEach((group) => {
        this.seedSelectSearchForGroup(group);
      });
    });
  }

  private seedSelectSearchForGroup(group: QuestionGroup): void {
    group.fields?.forEach((field) => {
      if ((field as any).type === 'select-search' || (field as any).type === 'third-party-search') {
        const key = this.getSelectSearchFieldKey(field);
        const control = this.getFieldControl(field);
        const value = control?.value;

        if (value) {
          // Initialize arrays if not exists
          if (!this.selectSearchSelectedValues[key]) {
            this.selectSearchSelectedValues[key] = [];
          }
          if (!this.selectSearchValueMappings[key]) {
            this.selectSearchValueMappings[key] = {};
          }

          // Handle both array and single values
          const values = Array.isArray(value) ? value : [value];
          const existingOptions =
            this.selectSearchOptions[key] || field.options || [];

          values.forEach((val) => {
            const match = existingOptions.find((opt: any) => opt.value === val);
            if (match) {
              this.selectSearchSelectedValues[key].push(match);
            } else {
              // Create a placeholder option
              this.selectSearchSelectedValues[key].push({
                value: val,
                label: val,
              });
            }
          });

          // In single-select mode, show the restored value in the input (View/Edit)
          if (field.singleSelect && !this.selectSearchQueries[key]) {
            const firstSelected = this.selectSearchSelectedValues[key][0];
            if (firstSelected?.label) {
              this.selectSearchQueries[key] = String(firstSelected.label);
            }
          }

          // NEW: Create inverse mapping for preloaded data
          this.createInverseMappingForPreloadedData(field, values);
        }
      }
    });
    group.subgroups?.forEach((sub) => this.seedSelectSearchForGroup(sub));
  }

  /**
   * Creates inverse mapping for preloaded data by finding the child field value
   * This allows deletion to work correctly with preloaded data
   */
  private createInverseMappingForPreloadedData(
    field: FormField,
    parentValues: string[]
  ): void {
    const key = this.getSelectSearchFieldKey(field);

    // Try to find if we already have a child question ID stored
    // If not, we'll need to find it by searching for the child field
    let childQuestionId = this.selectSearchChildQuestionIds[key];

    if (!childQuestionId) {
      // Try to find the child field by convention or proximity
      // This is a heuristic approach - look for text fields near this select-search
      const foundChildId = this.findChildFieldForSelectSearch(field);
      if (foundChildId) {
        childQuestionId = foundChildId;
        this.selectSearchChildQuestionIds[key] = foundChildId;
      }
    }

    if (childQuestionId) {
      // Find the child field and get its value
      const allFields = this.getAllFormFields();
      const childField = allFields.find(
        (fieldInfo) => fieldInfo.field.id === childQuestionId
      );

      if (childField) {
        const childControl = this.getFieldControl(childField.field);
        const childValue = childControl?.value;

        if (childValue) {
          // Create inverse mapping
          const childValues =
            typeof childValue === 'string'
              ? childValue
                  .split(',')
                  .map((v: string) => v.trim())
                  .filter((v: string) => v !== '')
              : [];

          // Map each parent value to corresponding child value by position
          parentValues.forEach((parentVal, index) => {
            if (childValues[index]) {
              this.selectSearchValueMappings[key][parentVal] =
                childValues[index];
            } else {
              console.warn(
                `⚠️ No child value at index ${index} for parent: "${parentVal}"`
              );
            }
          });

          // Validate mapping counts
          if (parentValues.length !== childValues.length) {
          }
        }
      }
    }
  }

  /**
   * Attempts to find the child field for a select-search field
   * Uses heuristics like field proximity and naming conventions
   */
  private findChildFieldForSelectSearch(
    selectSearchField: FormField
  ): number | null {
    const allFields = this.getAllFormFields();

    // Look for patterns in model properties
    const selectSearchProperty =
      selectSearchField.modelProperty?.toLowerCase() || '';

    // Common patterns for child fields based on your example:
    // "system_name_processes" -> "system_id_processes"
    const childPatterns = [
      selectSearchProperty.replace('_name_', '_id_'),
      selectSearchProperty.replace('name_', 'id_'),
      selectSearchProperty.replace('_name', '_id'),
      selectSearchProperty.replace('name', 'id'),
    ].filter((pattern) => pattern !== '' && pattern !== selectSearchProperty);

    console.log(`Looking for child field for: ${selectSearchProperty}`);
    console.log(`Child patterns to match: `, childPatterns);

    // Find exact matching field by modelProperty
    for (const fieldInfo of allFields) {
      const field = fieldInfo.field;
      if (field.type === 'text' && field.modelProperty) {
        const fieldProperty = field.modelProperty.toLowerCase();

        // Check for exact match with patterns
        if (childPatterns.includes(fieldProperty)) {
          console.log(
            `✅ Found exact child field match: ${field.id} (${field.modelProperty})`
          );
          return field.id;
        }
      }
    }

    // Fallback: look for similar patterns in labels if modelProperty didn't work
    const selectSearchLabel = selectSearchField.label?.toLowerCase() || '';
    for (const fieldInfo of allFields) {
      const field = fieldInfo.field;
      if (field.type === 'text') {
        const fieldLabel = field.label?.toLowerCase() || '';

        // Check if labels suggest a parent-child relationship
        if (
          selectSearchLabel.includes('name') &&
          fieldLabel.includes('id') &&
          selectSearchLabel.replace('name', '').trim() ===
            fieldLabel.replace('id', '').trim()
        ) {
          console.log(
            `✅ Found child field by label pattern: ${field.id} (${field.label})`
          );
          return field.id;
        }
      }
    }

    console.warn(
      `❌ Could not find child field for: ${selectSearchField.id} (${selectSearchField.modelProperty})`
    );
    return null;
  }

  onSelectSearchFocus(field: FormField): void {
    const key = this.getSelectSearchFieldKey(field);
    const query = this.selectSearchQueries[key];

    if (
      query &&
      query.length >= 2 &&
      this.selectSearchOptions[key]?.length > 0
    ) {
      this.selectSearchDropdownOpen[key] = true;
    }
  }

  onSelectSearchBlur(field: FormField): void {
    const key = this.getSelectSearchFieldKey(field);
    // Use timeout to allow click on dropdown options
    setTimeout(() => {
      this.selectSearchDropdownOpen[key] = false;
    }, 200);
  }

  onSelectSearchOptionClick(field: FormField, option: any): void {
    const key = this.getSelectSearchFieldKey(field);
    const isThirdPartySearch = field.type === 'third-party-search';

    // Initialize array if not exists
    if (!this.selectSearchSelectedValues[key]) {
      this.selectSearchSelectedValues[key] = [];
    }

    // Single-select mode: replace selection and show label in the input
    if (this.isSelectSearchSingleMode(field)) {
      this.selectSearchSelectedValues[key] = [option];
      this.selectSearchQueries[key] = option.label;
      this.selectSearchDropdownOpen[key] = false;
      const control = this.getFieldControl(field);
      if (control) control.setValue(option.label);
      return;
    }

    // Check if option is already selected
    const selectedValues = this.selectSearchSelectedValues[key];

    if (isThirdPartySearch && selectedValues.length >= 100) {
      this.dialog.open(BasicDialogComponent, {
        data: {
          uiTitle: 'Selection Limit Reached',
          uiMessage:
            'A maximum of 100 values can be selected for this question.',
          error: { icon: 'error_outline' },
          showCancel: false,
          showConfirm: true,
          confirmText: 'Close',
        } as SimpleDialog,
        width: '480px',
        panelClass: ['custom-dialog-border'],
      });
      return;
    }

    if (
      isThirdPartySearch &&
      selectedValues.some(
        (item) =>
          String(item?.value ?? '').trim().toLowerCase() ===
          String(option?.value ?? '').trim().toLowerCase()
      )
    ) {
      this.dialog.open(BasicDialogComponent, {
        data: {
          uiTitle: 'Duplicate Value',
          uiMessage: 'Duplicate values are not allowed for this question.',
          error: { icon: 'error_outline' },
          showCancel: false,
          showConfirm: true,
          confirmText: 'Close',
        } as SimpleDialog,
        width: '480px',
        panelClass: ['custom-dialog-border'],
      });
      return;
    }

    const existingIndex = selectedValues.findIndex(
      (item) => item.value === option.value
    );

    if (existingIndex === -1) {
      // Add new selection FIRST
      selectedValues.push(option);

      // Clear search query after selection
      this.selectSearchQueries[key] = '';
      this.selectSearchDropdownOpen[key] = false;

      // Update form control with array of selected values
      const control = this.getFieldControl(field);
      if (control) {
        const values = selectedValues.map((item) => item.value);
        control.setValue(values);
      }

      // Call option mapping endpoint with the selected option's ID and the field context
      // Pass the specific option that was just selected
      // (not applicable for third-party-search which has no child question mapping)
      if (field.type !== 'third-party-search') {
        this.fetchOptionMappingForSpecificOption(option, field);
      }
    } else if (!isThirdPartySearch) {
      // Remove if already selected (toggle behavior)
      this.removeSelectSearchValueInternal(field, option, key, existingIndex);
    }
  }

  private async performSelectSearch(
    field: FormField,
    query: string
  ): Promise<void> {
    const key = this.getSelectSearchFieldKey(field);

    try {
      this.selectSearchLoading[key] = true;

      // Call the API with the field's question ID
      const response = await this._ropaService.searchQuestionOptions(
        field.id,
        query
      );

      if (response && response.options) {
        this.selectSearchOptions[key] = response.options;

        // Store the question_child_id for later use
        if (response.question_child_id) {
          this.selectSearchChildQuestionIds[key] = response.question_child_id;
        }
      } else {
        this.selectSearchOptions[key] = [];
      }
    } catch (error) {
      console.error('Error searching options:', error);
      this.selectSearchOptions[key] = [];
    } finally {
      this.selectSearchLoading[key] = false;
    }
  }

  private async performThirdPartySearch(
    field: FormField,
    query: string
  ): Promise<void> {
    const key = this.getSelectSearchFieldKey(field);
    try {
      this.selectSearchLoading[key] = true;
      const raw = await this._ropaService.searchThirdPartyProviders(query);
      const results: any[] = Array.isArray(raw)
        ? raw
        : Array.isArray((raw as any)?.providers)
          ? (raw as any).providers
          : Array.isArray((raw as any)?.data)
            ? (raw as any).data
            : Array.isArray((raw as any)?.results)
              ? (raw as any).results
              : Array.isArray((raw as any)?.items)
                ? (raw as any).items
                : [];
      this.selectSearchOptions[key] = results.map((p) => ({
        id: p.id,
        value: p.third_party_name,
        label: p.third_party_name,
      }));
    } catch (error: any) {
      console.error('Error searching third-party providers:', error);
      this.selectSearchOptions[key] = [];
      const status: number = error?.status ?? 0;
      let userMessage: string;
      if (status === 400) {
        userMessage =
          error?.error?.message ??
          'The search query is invalid. Please enter at least 2 characters.';
      } else if (status === 500) {
        userMessage =
          error?.error?.message ??
          'An internal server error occurred. Please try again later.';
      } else {
        userMessage =
          'Could not connect to the server. Please check your network connection and try again.';
      }
      this.dialog.open(BasicDialogComponent, {
        data: {
          uiTitle: 'Search Error',
          uiMessage: userMessage,
          error: { icon: 'error_outline' },
          showCancel: false,
          showConfirm: true,
          confirmText: 'Close',
        } as SimpleDialog,
        width: '480px',
        panelClass: ['custom-dialog-border'],
      });
    } finally {
      this.selectSearchLoading[key] = false;
    }
  }

  /**
   * Fetches option mapping data for a specific option that was just selected
   */
  private async fetchOptionMappingForSpecificOption(
    selectedOption: any,
    field: FormField
  ): Promise<void> {
    try {
      const mappingData = await this._ropaService.getOptionMapping(
        selectedOption.id
      );

      // Get the child question ID from the stored search results
      const key = this.getSelectSearchFieldKey(field);
      const childQuestionId = this.selectSearchChildQuestionIds[key];

      if (childQuestionId && mappingData && mappingData.value) {
        // Store the mapping for the specific option that was just selected
        if (!this.selectSearchValueMappings[key]) {
          this.selectSearchValueMappings[key] = {};
        }

        // Store the mapping using the option's value as key
        this.selectSearchValueMappings[key][selectedOption.value] =
          mappingData.value;

        console.log(
          `Stored mapping: ${selectedOption.value} -> ${mappingData.value}`
        );
        console.log(
          'All mappings for field:',
          this.selectSearchValueMappings[key]
        );

        // Update child field by appending the new value
        this.appendToChildFieldValue(childQuestionId, mappingData.value);
      }
    } catch (error) {
      console.error('Error fetching option mapping:', error);
      // Handle error gracefully - don't break the user experience
    }
  }

  /**
   * Fetches option mapping data when an option is selected (legacy method)
   * Updates the child question field with the mapping value (concatenated for multiple selections)
   */
  private async fetchOptionMapping(
    optionId: number,
    field: FormField
  ): Promise<void> {
    try {
      const mappingData = await this._ropaService.getOptionMapping(optionId);

      // Get the child question ID from the stored search results
      const key = this.getSelectSearchFieldKey(field);
      const childQuestionId = this.selectSearchChildQuestionIds[key];

      if (childQuestionId && mappingData && mappingData.value) {
        // Store the mapping for later reference
        if (!this.selectSearchValueMappings[key]) {
          this.selectSearchValueMappings[key] = {};
        }

        // Find the option that was just selected to get its value
        const selectedValues = this.selectSearchSelectedValues[key] || [];
        const lastSelectedOption = selectedValues[selectedValues.length - 1];
        if (lastSelectedOption) {
          this.selectSearchValueMappings[key][lastSelectedOption.value] =
            mappingData.value;
        }

        // Update child field by appending the new value
        this.appendToChildFieldValue(childQuestionId, mappingData.value);
      }
    } catch (error) {
      console.error('Error fetching option mapping:', error);
      // Handle error gracefully - don't break the user experience
    }
  }

  /**
   * Updates a child field value based on the question ID
   */
  private updateChildFieldValue(questionId: number, value: any): void {
    // Find the field with the matching question ID in all form steps
    const allFields = this.getAllFormFields();

    const targetField = allFields.find(
      (fieldInfo) => fieldInfo.field.id === questionId
    );

    if (targetField) {
      const control = this.getFieldControl(targetField.field);
      if (control) {
        control.setValue(value);
      }
    } else {
      console.warn(`Could not find field with question ID: ${questionId}`);
    }
  }

  /**
   * Appends a value to a child field (for multi-select scenarios)
   */
  private appendToChildFieldValue(questionId: number, value: any): void {
    // Find the field with the matching question ID in all form steps
    const allFields = this.getAllFormFields();

    const targetField = allFields.find(
      (fieldInfo) => fieldInfo.field.id === questionId
    );

    if (targetField) {
      const control = this.getFieldControl(targetField.field);
      if (control) {
        const currentValue = control.value || '';
        const newValue = currentValue ? `${currentValue}, ${value}` : value;
        control.setValue(newValue);
      }
    } else {
      console.warn(`Could not find field with question ID: ${questionId}`);
    }
  }

  /**
   * Updates child fields after removing a selection (simplified version)
   */
  private updateChildFieldsAfterRemoval(field: FormField): void {
    // This method is now mostly handled by removeSpecificValueFromChildField
    // But we keep it for any edge cases or fallback scenarios
    console.warn('Using fallback method updateChildFieldsAfterRemoval');
  }

  /**
   * Gets all fields from all steps and groups for searching
   */
  private getAllFormFields(): Array<{
    field: FormField;
    step: FormStep;
    group: QuestionGroup;
  }> {
    const allFields: Array<{
      field: FormField;
      step: FormStep;
      group: QuestionGroup;
    }> = [];

    if (this.ropaForm.steps) {
      this.ropaForm.steps.forEach((step) => {
        step.questionGroups.forEach((group) => {
          this.extractFieldsFromGroup(group, step, allFields);
        });
      });
    }

    return allFields;
  }

  /**
   * Recursively extracts fields from groups and subgroups
   */
  private extractFieldsFromGroup(
    group: QuestionGroup,
    step: FormStep,
    allFields: Array<{ field: FormField; step: FormStep; group: QuestionGroup }>
  ): void {
    // Add fields from current group
    if (group.fields) {
      group.fields.forEach((field) => {
        allFields.push({ field, step, group });
      });
    }

    // Add fields from subgroups
    if (group.subgroups) {
      group.subgroups.forEach((subgroup) => {
        this.extractFieldsFromGroup(subgroup, step, allFields);
      });
    }
  }

  /**
   * Gets selected values for a select-search field
   */
  getSelectSearchSelectedValues(field: FormField): any[] {
    const key = this.getSelectSearchFieldKey(field);
    return this.selectSearchSelectedValues[key] || [];
  }

  /**
   * Removes a selected value from select-search field
   */
  removeSelectSearchValue(field: FormField, valueToRemove: any): void {
    const key = this.getSelectSearchFieldKey(field);
    const selectedValues = this.selectSearchSelectedValues[key] || [];

    const index = selectedValues.findIndex(
      (item) => item.value === valueToRemove.value
    );
    if (index > -1) {
      this.removeSelectSearchValueInternal(field, valueToRemove, key, index);

      this.dynamicFormGroup.markAsDirty();
      this.dynamicFormGroup.updateValueAndValidity();
      this.cdr.markForCheck();
      this.cdr.detectChanges();

      setTimeout(() => {}, 0);
    }
  }

  /**
   * Internal method to remove a select-search value and update child field
   */
  private removeSelectSearchValueInternal(
    field: FormField,
    valueToRemove: any,
    key: string,
    index: number
  ): void {
    const selectedValues = this.selectSearchSelectedValues[key] || [];

    // Get the mapped value before removing the selection
    const mappedValue =
      this.selectSearchValueMappings[key]?.[valueToRemove.value];

    console.log(`🗑️ Removing parent value: "${valueToRemove.value}"`);
    console.log(`🎯 Mapped to child value: "${mappedValue}"`);
    console.log(`📊 Total selections before removal: ${selectedValues.length}`);

    // Remove from selected values
    selectedValues.splice(index, 1);
    console.log(`📊 Total selections after removal: ${selectedValues.length}`);

    // Update form control
    const control = this.getFieldControl(field);
    if (control) {
      const values = selectedValues.map((item) => item.value);
      control.setValue(values);
      control.markAsDirty();
      control.markAsTouched();
      this.dynamicFormGroup.updateValueAndValidity();
      console.log(`✅ Updated parent field with values:`, values);
    }

    // Remove the specific mapped value from child field
    if (mappedValue) {
      this.removeSpecificValueFromChildField(
        field,
        valueToRemove.value,
        mappedValue
      );
    } else {
      console.warn(`❌ No mapped value found for: "${valueToRemove.value}"`);
      console.warn(
        'Available mappings:',
        Object.keys(this.selectSearchValueMappings[key] || {})
      );
      console.warn('This might be due to:');
      console.warn('1. Data was loaded but mapping was not created properly');
      console.warn('2. The child field was not found during initialization');
      console.warn('3. Mismatch in parent/child value counts');
    }
  }

  /**
   * Removes a specific mapped value from the child field
   */
  private removeSpecificValueFromChildField(
    field: FormField,
    optionValue: string,
    mappedValue: string
  ): void {
    const key = this.getSelectSearchFieldKey(field);
    const childQuestionId = this.selectSearchChildQuestionIds[key];

    if (childQuestionId) {
      // Find the child field
      const allFields = this.getAllFormFields();
      const targetField = allFields.find(
        (fieldInfo) => fieldInfo.field.id === childQuestionId
      );

      if (targetField) {
        const control = this.getFieldControl(targetField.field);
        if (control) {
          const currentValue = control.value || '';
          console.log(`🔍 Child field current value: "${currentValue}"`);

          // Remove the specific mapped value from the comma-separated string
          const values = currentValue
            .split(',')
            .map((v: string) => v.trim())
            .filter((v: string) => v !== '');

          console.log(`📝 Child values before filtering:`, values);
          console.log(`🎯 Removing specific value: "${mappedValue}"`);

          const updatedValues = values.filter((v: string) => v !== mappedValue);
          console.log(`📝 Child values after filtering:`, updatedValues);

          // Join with proper formatting
          const newValue =
            updatedValues.length > 0 ? updatedValues.join(', ') : '';

          control.setValue(newValue);
          control.markAsDirty();
          control.markAsTouched();
          this.dynamicFormGroup.updateValueAndValidity();
          this.cdr.detectChanges();
          console.log(`✅ Child field updated to: "${newValue}"`);

          // Also remove from our mapping tracker
          if (this.selectSearchValueMappings[key]) {
            delete this.selectSearchValueMappings[key][optionValue];
            console.log(`🧹 Cleaned up mapping for: "${optionValue}"`);
          }

          // Summary
          const remainingMappings = Object.keys(
            this.selectSearchValueMappings[key] || {}
          ).length;
          console.log(`📊 Remaining mappings: ${remainingMappings}`);
        }
      }
    }
  }

  /**
   * Checks if an option is already selected in select-search
   */
  isSelectSearchOptionSelected(field: FormField, option: any): boolean {
    const key = this.getSelectSearchFieldKey(field);
    const selectedValues = this.selectSearchSelectedValues[key] || [];
    return selectedValues.some((item) => item.value === option.value);
  }

  openSaveForLaterConfirmationDialog(): void {
    const data: SimpleDialog = {
      uiTitle: 'Confirm Submission',
      uiMessage: `Are you sure you want to save your progress as a draft?`,
      showCancel: true,
      showConfirm: true,
      cancelText: 'Cancel',
      confirmText: 'Confirm',
    };

    const dialogRef = this.dialog.open(BasicDialogComponent, {
      data,
      width: '640px',
      panelClass: ['submission-confirm-dialog', 'custom-dialog-border'],
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.saveDraft();
      }
    });
  }

  onSaveForLater(): void {
    if (!this.isSaveForLaterEnabled) {
      this.openSaveForLaterMandatoryFieldsDialog();
      return;
    }
    this.saveDraft();
  }

  onSaveForLaterResultModalClose(): void {
    this.showSaveForLaterResultModal = false;
    this.router.navigate(['/dashboard']);
  }

  async updateDraft(): Promise<void> {
    if (!this.isSaveForLaterEnabled) {
      this.openSaveForLaterMandatoryFieldsDialog();
      return;
    } else {
      try {
        const draftData = this.buildSubmissionData(true);
        await this._ropaService.updateDraft(draftData, parseInt(this.ropaId));
        this.initialFormValues = JSON.parse(
          JSON.stringify(this.dynamicFormGroup.getRawValue())
        );

        // Stop autosave before navigating away
        this.stopAutosave();

        this.router.navigate(['/dashboard']);
      } catch (error) {
        console.error('Error updating draft:', error);
        this.error = 'Error updating draft. Please try again.';
      }
    }
  }

  private updateSaveForLaterState(): void {
    const businessProcessName = this.dynamicFormGroup.get(
      'business_process_name'
    )?.value;
    const businessSetArea = this.dynamicFormGroup.get('business_area')?.value;
    const businessProcessOwnerControl = this.dynamicFormGroup.get(
      'business_process_owner'
    );
    const businessProcessOwner = businessProcessOwnerControl?.value;

    const isBusinessProcessNameValid = this.hasFieldValue(businessProcessName);
    const isBusinessSetAreaValid = this.hasFieldValue(businessSetArea);
    const isBusinessProcessOwnerValid =
      this.hasFieldValue(businessProcessOwner) &&
      !!businessProcessOwnerControl &&
      businessProcessOwnerControl.valid;

    const wasEnabled = this._isSaveForLaterEnabled;
    this._isSaveForLaterEnabled =
      isBusinessProcessNameValid &&
      isBusinessSetAreaValid &&
      isBusinessProcessOwnerValid;

    // Manage autosave based on mandatory fields for draft editing
    const isDraft = !!(this.ropaId && !this.ropaVersion);
    if (isDraft && this.uiMode === 'editing') {
      if (this._isSaveForLaterEnabled && !wasEnabled) {
        // Mandatory fields just became valid, start autosave
        this.startAutosave();
      } else if (!this._isSaveForLaterEnabled && wasEnabled) {
        // Mandatory fields became invalid, stop autosave
        this.stopAutosave();
      }
    }
  }

  get isSaveForLaterEnabled(): boolean {
    return this._isSaveForLaterEnabled;
  }

  private hasFieldValue(value: any): boolean {
    if (value == null || value === undefined) {
      return false;
    }

    if (typeof value === 'string') {
      return value.trim() !== '';
    }

    if (Array.isArray(value)) {
      return value.length > 0;
    }

    return true;
  }

  /**
   * Starts the autosave interval for draft editing
   */
  private startAutosave(): void {
    // Clear any existing interval first
    this.stopAutosave();

    // Only start autosave if mandatory fields are populated
    if (this._isSaveForLaterEnabled) {
      this.autosaveInterval = setInterval(() => {
        this.performAutosave();
      }, this.AUTOSAVE_INTERVAL_MS);
    }
  }

  /**
   * Stops the autosave interval
   */
  private stopAutosave(): void {
    if (this.autosaveInterval) {
      clearInterval(this.autosaveInterval);
      this.autosaveInterval = null;
    }
  }

  /**
   * Performs a silent autosave without navigating away or showing confirmation
   */
  private async performAutosave(): Promise<void> {
    // Only autosave if we're editing a draft and mandatory fields are populated
    const isDraft = !!(this.ropaId && !this.ropaVersion);
    if (!isDraft || this.uiMode !== 'editing' || !this._isSaveForLaterEnabled) {
      return;
    }

    try {
      const draftData = this.buildSubmissionData(true);
      await this._ropaService.updateDraft(draftData, parseInt(this.ropaId));

      // Update initial values after successful autosave
      this.initialFormValues = JSON.parse(
        JSON.stringify(this.dynamicFormGroup.getRawValue())
      );
    } catch (error) {
      console.error('Error during autosave:', error);
      // Don't show error to user for silent autosave, just log it
    }
  }
}
